import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Container,
  Button,
  Table,
  Row,
  Col,
  Card,
  ProgressBar,
  Badge,
  Tabs,
  Tab,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { FaPlay, FaStop, FaPause } from "react-icons/fa6";
import { PulseLoader } from "react-spinners";
import {
  FaEdit,
  FaClock,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";

import AddInputModal from "./modals/AddInputModal";
import StoreFinishedProductModalEnhanced from "./modals/StoreFinishedProductModalEnhanced";
import StageUpdateModal from "./modals/StageUpdateModal";
import EditBatchLabelModal from "./modals/EditBatchLabelModal";
import StoredProductsDisplay from "./components/StoredProductsDisplay";

const formatRunTime = (seconds) => {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
};
const formatDateTime = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleString();
};

const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const BatchDetailsPage = () => {
  const { batchId } = useParams();
  const [batchDetails, setBatchDetails] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [totalRunTime, setTotalRunTime] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  const [showInputModal, setShowInputModal] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [storedProducts, setStoredProducts] = useState({ products: [], totalEntries: 0 });

  const [showStageModal, setShowStageModal] = useState(false);
  const [showEditLabelModal, setShowEditLabelModal] = useState(false);
  const [selectedStageIndex] = useState(null);
  const [selectedStageData] = useState(null);

  const fetchBatchDetails = useCallback(async (id) => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/production-batches/${id}`
      );
      const batch = res.data;
      setBatchDetails(batch);
      setIsRunning(batch.status === "in-progress");
      setIsPaused(checkIfPaused(batch.startStopEvents));
      calculateTotalRuntime(batch.startStopEvents);

      // Fetch stored products for this batch
      fetchStoredProducts(id);
    } catch (error) {
      toast.error("Failed to fetch batch details");
    }
  }, []);

  useEffect(() => {
    fetchBatchDetails(batchId);
  }, [batchId, fetchBatchDetails]);

  const fetchStoredProducts = async (batchId) => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/production-batches/${batchId}/stored-products`
      );
      setStoredProducts(res.data || { products: [], totalEntries: 0 });
    } catch (error) {
      console.error("Error fetching stored products:", error);
      setStoredProducts({ products: [], totalEntries: 0 });
    }
  };

  const checkIfPaused = (events) => {
    if (!events?.length) return false;
    const last = events[events.length - 1];
    return last.type === "pause";
  };

  const calculateTotalRuntime = (events = []) => {
    let total = 0;
    let lastStart = null;

    events.forEach(({ type, timestamp }) => {
      if (type === "start" || type === "resume") {
        lastStart = new Date(timestamp);
      } else if ((type === "pause" || type === "stop") && lastStart) {
        const end = new Date(timestamp);
        total += (end - lastStart) / 1000;
        lastStart = null;
      }
    });

    setTotalRunTime(total);
  };

  const handleStartStopBatch = async (action) => {
    // Show confirmation for stop action
    if (action === "stop") {
      const confirmed = window.confirm(
        "Are you sure you want to stop this batch?\n\n" +
        "⚠️ Warning: Once stopped, the batch process cannot be restarted.\n" +
        "Batch will be permanently stopped.\n\n" +
        "This action cannot be undone. Continue?"
      );

      if (!confirmed) {
        return; // User cancelled
      }
    }

    setActionLoading(true);
    try {
      const currentTime = new Date().toISOString();

      await axios.post(
        `${config.API_BASE_URL}/production-batches/${batchId}/${action}`,
        {
          batchId,
          timestamp: currentTime,
        }
      );

      toast.success(`Batch ${action}ed successfully!`);
      fetchBatchDetails(batchId);
    } catch (error) {
      toast.error(`Failed to ${action} batch`);
    } finally {
      setActionLoading(false);
    }
  };

  const calculateProjectedOutput = (productId) => {
    const runtimeHours = totalRunTime / 3600;
    const product = batchDetails.outputs.find(
      (o) => o.productId._id === productId
    );
    return product?.projectedOutputPerHour
      ? (runtimeHours * product.projectedOutputPerHour).toFixed(2)
      : "0.00";
  };



  const getRunTimeIntervals = (events) => {
    const intervals = [];
    let currentStart = null;

    events.forEach((event, index) => {
      if (event.type === "start" || event.type === "resume") {
        currentStart = new Date(event.timestamp);
      } else if (
        (event.type === "pause" || event.type === "stop") &&
        currentStart
      ) {
        const end = new Date(event.timestamp);
        const duration = (end - currentStart) / 1000;
        intervals.push({
          start: currentStart,
          end,
          duration,
          label: `${event.type.charAt(0).toUpperCase() + event.type.slice(1)}`,
        });
        currentStart = null;
      }
    });

    // Still running case
    if (currentStart) {
      const end = new Date(); // Now
      const duration = (end - currentStart) / 1000;
      intervals.push({
        start: currentStart,
        end,
        duration,
        label: "Running...",
      });
    }

    return intervals;
  };

  const runtimeIntervals = getRunTimeIntervals(
    batchDetails?.startStopEvents || []
  );

  if (!batchDetails)
    return (
      <Layout>
        <div className="text-center py-5">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      </Layout>
    );

  // Calculate totals for stop button restriction and wastage display
  const totalInput = batchDetails.inputs.reduce((sum, input) => sum + input.quantity, 0);
  const totalStored = storedProducts.products.reduce((sum, product) => sum + product.totalStored, 0);
  const sumPercent = batchDetails.outputs.reduce((sum, output) => sum + output.percentage, 0);
  const sumCalculated = batchDetails.outputs.reduce((sum, output) => sum + output.calculatedOutput, 0);
  const wastagePercent = 100 - sumPercent;
  const wastageQuantity = totalInput - sumCalculated;
  const unit = batchDetails.outputs[0]?.unit || batchDetails.inputs[0]?.unit || 'units';

  // console.log("total-stored: " + totalStored);
  // console.log("total-input: " + totalInput);
  // console.log("sum-percent: " + sumPercent);

  const totalInputQty = batchDetails.inputs.reduce(
    (sum, input) => sum + Number(input.quantity || 0),
    0
  );

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/batches">Production Batches</Link>
          </li>
          <li className="breadcrumb-item active">
            {batchDetails?.batchNumber}
          </li>
        </ol>
      </nav>

      <Row className="align-items-center mb-4 flex-column flex-md-row justify-content-between">
        <Col className="d-flex align-items-center justify-content-between justify-content-md-start w-100">
          <div className="d-flex align-items-center">
            <h2 className="me-3 mb-0">{batchDetails?.label || batchDetails?.batchNumber}</h2>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowEditLabelModal(true)}
              className="d-flex align-items-center"
              title="Edit batch label"
            >
              <FaEdit size={14} />
            </Button>
          </div>
        </Col>

        <Col className="text-end mt-3 mt-md-0">
          <Badge
            bg={
              batchDetails.status === "in-progress"
                ? "success"
                : batchDetails.status === "paused"
                  ? "warning"
                  : batchDetails.status === "completed"
                    ? "secondary"
                    : "dark"
            }
            className="fs-8 text-uppercase"
          >
            {batchDetails.status}
          </Badge>
        </Col>
      </Row>

      <Container fluid className="batch-details-page my-4 px-0">
        <Row>
          {/* LEFT SIDE - OVERVIEW */}
          <Col md={4} lg={12} xl={4} className="mb-4">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-primary-subtle">
                <Card.Title className="mb-0">Batch Overview</Card.Title>
              </Card.Header>
              <Card.Body>
                <p>
                  <strong>Batch Number:</strong> {batchDetails.batchNumber}
                </p>
                <p>
                  <strong>BOM:</strong> {batchDetails.bomId.name}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <Badge
                    bg={
                      batchDetails.status === "in-progress"
                        ? "success"
                        : batchDetails.status === "paused"
                          ? "warning"
                          : "secondary"
                    }
                  >
                    {batchDetails.status}
                  </Badge>
                </p>
                <p>
                  <strong>Total Runtime:</strong> {formatRunTime(totalRunTime)}
                </p>
                {storedProducts.products?.length > 0 && (
                  <p>
                    <strong>Products Stored:</strong>{" "}
                    <Badge bg="info">
                      {storedProducts.totalEntries} entries
                    </Badge>
                  </p>
                )}
                {batchDetails.status != "completed" && (
                  <div className="mt-3 d-grid gap-2">
                    {!isRunning && !isPaused ? (
                      <Button
                        variant="success"
                        disabled={actionLoading}
                        onClick={() => handleStartStopBatch("start")}
                        className="d-flex align-items-center justify-content-center"
                      >
                        {actionLoading ? (
                          <Spinner size="sm" className="me-2" />
                        ) : (
                          <FaPlay className="me-2" />
                        )}
                        Start Batch
                      </Button>
                    ) : isRunning ? (
                      <>
                        <Button
                          variant="warning"
                          disabled={actionLoading}
                          onClick={() => handleStartStopBatch("pause")}
                          className="d-flex align-items-center justify-content-center mb-2"
                        >
                          {actionLoading ? (
                            <Spinner size="sm" className="me-2" />
                          ) : (
                            <FaPause className="me-2" />
                          )}
                          Pause Batch
                        </Button>
                        {totalStored > totalInput * 0.90 && (
                          <Button
                            variant="danger"
                            disabled={actionLoading}
                            onClick={() => handleStartStopBatch("stop")}
                            className="d-flex align-items-center justify-content-center"
                          >
                            {actionLoading ? (
                              <Spinner size="sm" className="me-2" />
                            ) : (
                              <FaStop className="me-2" />
                            )}
                            Stop Batch
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button
                          variant="primary"
                          disabled={actionLoading}
                          onClick={() => handleStartStopBatch("resume")}
                          className="d-flex align-items-center justify-content-center"
                        >
                          {actionLoading ? (
                            <Spinner size="sm" className="me-2" />
                          ) : (
                            <FaPlay className="me-2" />
                          )}
                          Resume Batch
                        </Button>
                        {totalStored > totalInput * 0.90 && (
                          <Button
                            variant="danger"
                            disabled={actionLoading}
                            onClick={() => handleStartStopBatch("stop")}
                            className="d-flex align-items-center justify-content-center"
                          >
                            {actionLoading ? (
                              <Spinner size="sm" className="me-2" />
                            ) : (
                              <FaStop className="me-2" />
                            )}
                            Stop Batch
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* RIGHT SIDE - TABS */}
          <Col md={8} lg={12} xl={8}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <Tabs
                  defaultActiveKey="inputs"
                  id="batch-details-tabs"
                  className="mb-3"
                >
                  <Tab eventKey="productionRequests" title="Production Requests">
                    <h4 className="mb-3">Assigned Production Requests</h4>
                    {batchDetails.productionRequestIds && batchDetails.productionRequestIds.length > 0 ? (
                      <Table responsive bordered>
                        <thead>
                          <tr>
                            <th>Request #</th>
                            <th>Product</th>
                            <th>Quantity</th>
                            <th>Assigned To</th>
                            <th>Required Date</th>
                            <th>Requested By</th>
                            <th>Sales Orders</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchDetails.productionRequestIds.map((pr, idx) => (
                            <tr key={pr._id || idx}>
                              <td>{pr.requestNumber}</td>
                              <td>{pr.productId?.productName || '-'}</td>
                              <td>{pr.quantityRequired}</td>
                              <td>{pr.assignedTo?.name || '-'}</td>
                              <td>{pr.requiredDate ? new Date(pr.requiredDate).toLocaleDateString() : '-'}</td>
                              <td>{pr.requestedBy?.name || '-'}</td>
                              <td>
                                {pr.salesOrders && pr.salesOrders.length > 0 ? (
                                  <ul className="mb-0 ps-3">
                                    {pr.salesOrders.map((so, soIdx) => (
                                      <li key={soIdx}>
                                        {so.salesOrderId?.orderNumber || so.salesOrderId || '-'}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  '-'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-muted">No production requests assigned to this batch.</div>
                    )}
                  </Tab>

                  <Tab eventKey="inputs" title="Inputs">
                    <div className="mb-3 d-flex justify-content-between align-items-center gap-2">
                      <h4>Input Quantities</h4>
                      {batchDetails.status !== "completed" && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => setShowInputModal(true)}
                        >
                          + Add Input
                        </Button>
                      )}
                    </div>
                    <Table responsive bordered>
                      <thead>
                        <tr>
                          <th>Material</th>
                          <th>Quantity</th>
                          <th>Location</th>
                          <th>PO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchDetails.inputs.map((input, idx) => (
                          <tr key={idx}>
                            <td>{input.materialId?.productName}</td>
                            <td>
                              {input.quantity} {input.unit}
                            </td>
                            <td>{input.locationId?.label}</td>
                            <td>{input?.poId?.orderId}</td>
                          </tr>
                        ))}
                        {/* Total Row */}
                        <tr className="fw-bold bg-light">
                          <td colSpan={1}>Total</td>
                          <td>
                            {totalInputQty} {batchDetails.inputs[0]?.unit}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </Table>
                  </Tab>

                  <Tab eventKey="stages" title="Stages">
                    {/* <div className="stage-pipeline mb-4">
                      {batchDetails.stages.map((stage, idx) => (
                        <div key={idx} className="pipeline-step">
                          <div className={`step-circle ${stage.status === 'completed' ? 'completed' :
                              stage.status === 'in-progress' ? 'in-progress' : 'pending'
                            }`}>
                            {stage.status === 'completed' ? <FaCheckCircle /> : idx + 1}
                          </div>
                          <small className="step-label">{stage.stageName}</small>
                        </div>
                      ))}
                    </div> */}
                    <div className="row g-3">
                      {batchDetails.stages.map((stage, idx) => (
                        <div key={idx} className="col-md-6">
                          <Card className="h-100 border-0 shadow-sm hover-shadow transition-all">
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                  <h6 className="mb-1 fw-bold">
                                    {stage.stageName}
                                  </h6>
                                  <small className="text-muted">
                                    Stage {idx + 1}
                                  </small>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  {/* <Badge
                                    bg={
                                      stage.status === "completed"
                                        ? "success"
                                        : stage.status === "in-progress"
                                          ? "warning"
                                          : "secondary"
                                    }
                                    className="d-flex align-items-center gap-1"
                                  >
                                    {stage.status === "completed" ? (
                                      <FaCheckCircle size={12} />
                                    ) : stage.status === "in-progress" ? (
                                      <FaClock size={12} />
                                    ) : (
                                      <FaExclamationCircle size={12} />
                                    )}
                                    {stage.status}
                                  </Badge> */}
                                  {/* <Button
                                    variant="light"
                                    size="sm"
                                    onClick={() => openStageModal(idx)}
                                    className="pt-0 px-2"
                                  >
                                    <FaEdit size={12} />
                                  </Button> */}
                                </div>
                              </div>

                              {stage.progress > 0 && (
                                <div className="mb-3">
                                  <div className="d-flex justify-content-between mb-1">
                                    <small className="text-muted">
                                      Progress
                                    </small>
                                    <small className="fw-bold">
                                      {stage.progress}%
                                    </small>
                                  </div>
                                  <ProgressBar
                                    now={stage.progress}
                                    variant={
                                      stage.progress === 100
                                        ? "success"
                                        : stage.progress > 50
                                          ? "info"
                                          : "warning"
                                    }
                                    style={{ height: "6px" }}
                                  />
                                </div>
                              )}

                              <div className="text-muted small">
                                <strong>Notes:</strong>{" "}
                                {stage.notes || "No notes available"}
                              </div>

                              {(stage.startedAt || stage.endedAt) && (
                                <div className="mt-2 pt-2 border-top">
                                  {stage.startedAt && (
                                    <div className="small text-muted mb-1">
                                      <strong>Started:</strong>{" "}
                                      {formatDateTime(stage.startedAt)}
                                    </div>
                                  )}
                                  {stage.endedAt && (
                                    <div className="small text-muted">
                                      <strong>Ended:</strong>{" "}
                                      {formatDateTime(stage.endedAt)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </Tab>

                  <Tab eventKey="timeline" title="Timeline">
                    {runtimeIntervals.length === 0 ? (
                      <div className="text-center py-5">
                        <FaClock size={48} className="text-muted mb-3" />
                        <p className="text-muted">
                          No run events recorded yet.
                        </p>
                      </div>
                    ) : (
                      <div className="timeline-container">
                        {runtimeIntervals.map((interval, idx) => (
                          <Card key={idx} className="mb-3 border-0 shadow-sm">
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="timeline-icon me-3">
                                      {interval.label === "Pause" ? (
                                        <FaPause className="text-warning" />
                                      ) : interval.label === "Stop" ? (
                                        <FaStop className="text-danger" />
                                      ) : interval.label === "Running..." ? (
                                        <div className="spinner-border spinner-border-sm text-success" />
                                      ) : (
                                        <FaPlay className="text-success" />
                                      )}
                                    </div>
                                    <div>
                                      <h6 className="mb-0">
                                        {interval.label === "Running..."
                                          ? "Currently Running"
                                          : interval.label}
                                      </h6>
                                      <small className="text-muted">
                                        Session {idx + 1}
                                      </small>
                                    </div>
                                  </div>

                                  <div className="row text-sm">
                                    <div className="col-md-6">
                                      <div className="mb-1">
                                        <strong className="text-success">
                                          Started:
                                        </strong>
                                        <br />
                                        <span className="text-muted">
                                          {formatDateTime(interval.start)}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="mb-1">
                                        <strong
                                          className={
                                            interval.label === "Pause"
                                              ? "text-warning"
                                              : interval.label === "Stop"
                                                ? "text-danger"
                                                : "text-info"
                                          }
                                        >
                                          {interval.label === "Running..."
                                            ? "Current Time:"
                                            : "Ended:"}
                                        </strong>
                                        <br />
                                        <span className="text-muted">
                                          {formatDateTime(interval.end)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="text-end">
                                  <Badge bg="primary" className="fs-9 p-2">
                                    <FaClock className="me-1" />
                                    {formatDuration(interval.duration)}
                                  </Badge>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )}
                  </Tab>

                  <Tab eventKey="outputs" title="Outputs & Storage">
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <h5>Expected Outputs (from BOM)</h5>
                      {(batchDetails.status === "in-progress" ||
                        batchDetails.status === "paused" ||
                        batchDetails.status !== "completed") && (
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => setShowStoreModal(true)}
                          >
                            + Store Finished Product
                          </Button>
                        )}
                    </div>
                    <Table responsive bordered className="mb-4">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Percentage</th>
                          <th>Calculated Output</th>
                          <th>Projected Output</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchDetails.outputs.sort((a, b) => b.percentage - a.percentage).map((output, idx) => (
                          <tr key={idx}>
                            <td>{output.productId?.productName}</td>
                            <td>{output.percentage}%</td>
                            <td>
                              {output.calculatedOutput?.toFixed(2)}{" "}
                              {output.unit}
                            </td>
                            <td>
                              {calculateProjectedOutput(output.productId._id)}{" "}
                              {output.unit}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {sumPercent < 100 && (
                      <div className="mb-3 text-muted">
                        <strong>Wastage:</strong> {wastagePercent.toFixed(2)}% ({wastageQuantity.toFixed(2)} {unit})
                      </div>
                    )}

                    {/* Enhanced Stored Products Display */}
                    <div className="mt-4">
                      <StoredProductsDisplay storedProducts={storedProducts} />
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <AddInputModal
          show={showInputModal}
          handleClose={() => setShowInputModal(false)}
          batchId={batchDetails._id}
          refreshBatch={() => fetchBatchDetails(batchDetails._id)}
          bomId={batchDetails.bomId._id}
        />

        <StoreFinishedProductModalEnhanced
          show={showStoreModal}
          handleClose={() => setShowStoreModal(false)}
          batchId={batchDetails._id}
          batchDetails={batchDetails}
          refreshBatch={() => fetchBatchDetails(batchDetails._id)}
        />

        <StageUpdateModal
          show={showStageModal}
          handleClose={() => setShowStageModal(false)}
          batchId={batchDetails._id}
          stageIndex={selectedStageIndex}
          stageData={selectedStageData}
          refreshBatch={() => fetchBatchDetails(batchDetails._id)}
        />

        <EditBatchLabelModal
          show={showEditLabelModal}
          handleClose={() => setShowEditLabelModal(false)}
          batchId={batchDetails._id}
          currentLabel={batchDetails?.label || ""}
          refreshBatch={() => fetchBatchDetails(batchDetails._id)}
        />
      </Container>

      <style jsx>{`
        .hover-shadow {
          transition: all 0.3s ease;
        }
        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          transform: translateY(-2px);
        }
        .transition-all {
          transition: all 0.3s ease;
        }
        .timeline-container {
          position: relative;
        }
        .timeline-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #e9ecef;
        }
        .stage-pipeline {
          position: relative;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }
        .stage-pipeline::before {
          content: '';
          position: absolute;
          top: 20px;
          left: 50px;
          right: 50px;
          height: 2px;
          background: #dee2e6;
          z-index: 0;
        }
        .pipeline-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          position: relative;
          z-index: 1;
        }
        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
          border: 2px solid transparent;
        }
        .step-circle.completed {
          background: #198754;
          color: white;
        }
        .step-circle.in-progress {
          background: #ffc107;
          color: black;
        }
        .step-circle.pending {
          background: #6c757d;
          color: white;
        }
        .step-label {
          margin-top: 0.5rem;
          text-align: center;
          font-size: 0.875rem;
        }
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
        .badge {
          animation: pulse 2s infinite;
        }
      `}</style>
    </Layout>
  );
};

export default BatchDetailsPage;
